<template>
  <v-container fluid>
    <UniversalTable
      v-if="!isLoading"
      :config="tableConfig.tableParams.value"
      :items="employees.allEmployees.value"
      :backendPagination="true"
      :itemsCount="employeeStore.getTotalEmployeesCount"
      :externalCurrentPage="employeeStore.getPagination.currentPage"
      :department-filter="
        viewState.isReadinessFilterBarMdp552Enabled.value
          ? viewState.state.filters.department
          : null
      "
      :department-scope="viewState.state.filters.departmentScope"
      @toggleDepartmentScope="viewState.toggleDepartmentScope()"
      @clearDepartmentFilter="viewState.clearDepartmentFilter()"
      @onSearch="viewState.handleSearch"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @removeFilter="viewState.removeFilters"
      @removeAllFilters="viewState.clearFilters"
      @onRequest="handlePaginationRequest"
      :emptyObjectData="emptyObjectData"
      @onActionClick="viewState.recalculateAllEmployees"
    >
    </UniversalTable>
    <FilterEmployeesReadinessModal
      v-if="viewState.isReadinessFilterBarMdp552Enabled.value"
      :value="viewState.state.filters.showFilterDialog"
      :initial-excluded="viewState.getInitialExcluded()"
      :initial-department="viewState.state.filters.department"
      :initial-scope="viewState.state.filters.departmentScope"
      :departments="viewState.state.filters.departments"
      :loading="viewState.state.filters.departmentsLoading"
      @input="viewState.state.filters.showFilterDialog = $event"
      @close="viewState.state.filters.showFilterDialog = false"
      @search="viewState.loadDepartments"
      @apply="viewState.applyReadinessFilterModal"
    />
    <SearchModal
      v-else
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Employee Readiness"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="[]"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />

    <ChecklistDrawer
      :isEmployeeChatter="true"
      ref="drawerContainer"
      :drawer="isDrawerOpen"
      :avatar="{
        picture: employee.getCurrentEmployee()?.avatar,
        firstName: employee.getCurrentEmployee()?.first_name || '',
        lastName: employee.getCurrentEmployee()?.last_name || '',
      }"
      :typeValue="
        employee.getCurrentEmployee()?.first_name + ' ' + employee.getCurrentEmployee()?.last_name
      "
      @closeDrawer="viewState.closeEmployeeChatterModal()"
      :type="'Employee'"
      object-type="employee"
      :object-id="employee.getCurrentEmployee()?.id ?? null"
      :chatterList="viewState.state?.openChatterModal?.chatterData"
      :totalCheckpointChatter="viewState.state?.openChatterModal?.totalEmployeeChatter"
      @requestMoreCheckpointsChatter="viewState.requestMoreChatter()"
    />
  </v-container>
</template>

<script setup>
import { watch, onMounted, computed, getCurrentInstance } from "vue";
import { useEmployees } from "@/composables/employees/useEmployees";
import { useReadinessEmployees } from "@/composables/readinessEmployees/useReadinessEmployees";
import { useEmployeeStore } from "@/store/pinia/employee";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import { useTableConfig } from "@/views/ReadinessEmployees/composables/useReadinessEmployeeTableConfig";
import { useReadinessEmployeeViewState } from "@/views/ReadinessEmployees/composables/useReadinessEmployeeViewState";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import FilterEmployeesReadinessModal from "@components/FilterEmployeesReadinessModal/FilterEmployeesReadinessModal.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";

const tableConfig = useTableConfig();
const viewState = useReadinessEmployeeViewState();
const employees = useReadinessEmployees();
const employee = useEmployees();
const employeeStore = useEmployeeStore();
const app = getCurrentInstance();
const { proxy } = app;

const emptyObjectData = {
  singular: "Readiness Employee",
  plural: "Readiness Employees",
};

// Create a computed property for drawer visibility
const isDrawerOpen = computed(() => {
  const isOpen = viewState.state.openChatterModal.show;
  return isOpen;
});

// Watch for changes in drawer state
watch(() => viewState.state?.openChatterModal?.show);

watch(() => employee.getCurrentEmployee());

// Computed property to track app loader state
const isLoading = computed(() => {
  return proxy.$store?.state?.isLoading?.isLoading || false;
});

// Handle pagination requests from UniversalTable
const handlePaginationRequest = async (paginationData) => {
  try {
    proxy.$loader.show();
    // Update pagination state first
    viewState.state.table.currentPage = paginationData.page;
    viewState.state.table.itemsPerPage = paginationData.itemsPerPage;
    // Load employees with current filters to preserve query parameters
    await viewState.loadEmployeesWithFilters();
  } finally {
    proxy.$loader.hide();
  }
};

// Watch for filter changes to reload data
watch(
  () => viewState.state.filters.values,
  async () => {
    try {
      proxy.$loader.show();
      await viewState.loadEmployeesWithFilters();
    } finally {
      proxy.$loader.hide();
    }
  },
  { deep: true }
);

// Watch employees to update needsRecalculation flag
watch(
  () => employees.allEmployees.value,
  (employeesList) => {
    viewState.state.filters.needsRecalculation = employeesList.some(
      (employee) => employee.needs_recalc === true
    );
  },
  { deep: true, immediate: true }
);

onMounted(async () => {
  try {
    proxy.$loader.show();
    // Initialize view state (resets pagination)
    viewState.initializeViewState();
    // Initialize feature flags first
    await viewState.initializeFeatureFlags();
    await viewState.loadEmployeesWithFilters();
  } finally {
    proxy.$loader.hide();
  }
});
</script>

<style scoped>
.icon-container {
  cursor: pointer;
}
</style>
